// src/screens/RoadmapScreen.js
//
// Public-facing roadmap tab. Shows what's being built next so users know
// what's coming and can look forward to future releases.

import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts, Typography } from '../theme';

// ─── Roadmap data ────────────────────────────────────────────────────────────

const ITEMS = [
  {
    id: 'mobile-android',
    icon: 'logo-android',
    title: 'Android App',
    description: 'SleepMixBlueprint is live on Google Play — download it and take your mixes off the browser, with offline playback so the mix keeps playing even without a connection.',
    status: 'LIVE',
  },
  {
    id: 'ai',
    icon: 'sparkles-outline',
    title: 'AI-Powered Personalization',
    description: "Mixes that adapt to how you're actually sleeping — learning from wearable sleep-stage data to fine-tune your blend in real time.",
    status: 'IN PROGRESS',
  },
  {
    id: 'wearable',
    icon: 'watch-outline',
    title: 'Wearable Integrations',
    description: 'Connect Oura, Apple Watch, and other sleep trackers so your blueprint responds to your body, not just the clock.',
    status: 'PLANNED',
  },
  {
    id: 'smarthome',
    icon: 'home-outline',
    title: 'Smart Home Compatibility',
    description: 'Trigger your saved mix by voice through Alexa, Google Home, or HomeKit — no phone required at bedtime.',
    status: 'PLANNED',
  },
  {
    id: 'premium',
    icon: 'star-outline',
    title: 'Premium Subscriptions',
    description: "A Pro tier with the full sound library, offline mode, and unlimited saved blueprints — for the nights the free tier isn't quite enough.",
    status: 'PLANNED',
  },
  {
    id: 'mobile-ios',
    icon: 'logo-apple',
    title: 'iOS App',
    description: 'SleepMixBlueprint for iPhone and iPad — not yet available, but on the roadmap.',
    status: 'PLANNED',
  },
];

const STATUS_CONFIG = {
  'IN PROGRESS': {
    color: Colors.accent,
    bg: Colors.accent + '1A',
    icon: 'ellipse',
  },
  LIVE: {
    color: '#4ade80',
    bg: '#4ade8022',
    icon: 'checkmark-circle',
  },
  PLANNED: {
    color: Colors.textMuted,
    bg: Colors.textMuted + '1A',
    icon: 'ellipse-outline',
  },
};

// ─── Components ──────────────────────────────────────────────────────────────

function RoadmapCard({ icon, title, description, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PLANNED;

  return (
    <View style={styles.card}>
      {/* Icon bubble */}
      <View style={styles.iconBubble}>
        <Ionicons name={icon} size={22} color={Colors.accent} />
      </View>

      {/* Content */}
      <View style={styles.cardBody}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>{title}</Text>
          <View style={[styles.badge, { backgroundColor: cfg.bg }]}>
            <Ionicons name={cfg.icon} size={7} color={cfg.color} style={{ marginRight: 4 }} />
            <Text style={[styles.badgeText, { color: cfg.color }]}>{status}</Text>
          </View>
        </View>
        <Text style={styles.cardDescription}>{description}</Text>
      </View>
    </View>
  );
}

// ─── Screen ──────────────────────────────────────────────────────────────────

export default function RoadmapScreen() {
  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Hero */}
      <View style={styles.hero}>
        <View style={styles.heroIconRow}>
          <View style={styles.heroBadge}>
            <Ionicons name="map-outline" size={18} color={Colors.accent} />
          </View>
          <Text style={styles.eyebrow}>ROADMAP</Text>
        </View>
        <Text style={styles.heroTitle}>Sleep architecture is never really finished.</Text>
        <Text style={styles.heroLede}>
          SleepMixBlueprint is actively evolving. Here is what is coming next — from AI-powered
          personalization to wearable integrations and beyond.
        </Text>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Cards */}
      <View style={styles.list}>
        {ITEMS.map((item) => (
          <RoadmapCard key={item.id} {...item} />
        ))}
      </View>

      {/* Footer */}
      <Text style={styles.footer}>Built one mix at a time — by Tyneil Jene's</Text>
    </ScrollView>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },

  /* Hero */
  hero: {
    paddingTop: 8,
    paddingBottom: 24,
  },
  heroIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },
  heroBadge: {
    width: 34,
    height: 34,
    borderRadius: Radius.sm,
    backgroundColor: Colors.accent + '22',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyebrow: {
    fontFamily: Fonts.bold,
    fontSize: 11,
    letterSpacing: 3,
    color: Colors.accent,
  },
  heroTitle: {
    fontFamily: Fonts.bold,
    fontSize: 22,
    color: Colors.textBright,
    lineHeight: 30,
    marginBottom: 12,
  },
  heroLede: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textSecondary,
    lineHeight: 22,
  },

  /* Divider */
  divider: {
    height: 1,
    backgroundColor: Colors.border,
    marginBottom: 20,
  },

  /* Card list */
  list: {
    gap: 12,
  },
  card: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 18,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
  },
  iconBubble: {
    width: 44,
    height: 44,
    borderRadius: Radius.md,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  cardBody: {
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 6,
  },
  cardTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    color: Colors.textBright,
    flex: 1,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: Radius.full,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexShrink: 0,
  },
  badgeText: {
    fontFamily: Fonts.bold,
    fontSize: 9,
    letterSpacing: 0.8,
  },
  cardDescription: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 20,
  },

  /* Footer */
  footer: {
    marginTop: 32,
    fontSize: 12,
    fontFamily: Fonts.regular,
    color: Colors.textMuted,
    textAlign: 'center',
  },
});
